<app-card class="page-container">
  <app-card-content>
    @if (loading()) {
    <div class="flex flex-column flex-align-center gap-lg p-xl">
      <app-spinner />
    </div>
    } @else if (loadError()) {
    <div class="flex flex-column flex-align-center gap-lg p-xl text-center" data-testid="nfc-load-error">
      <app-icon name="alert-circle" />
      <p>{{ loadError() }}</p>
    </div>
    } @else {
    <div class="flex flex-column gap-lg p-md">
      <div class="flex flex-column gap-xs">
        <h2 class="nfc-title">{{ 'nfcTags.title' | translate }}</h2>
        <p class="nfc-subtitle">{{ checkPoint()?.name }} · {{ form()?.name }}</p>
        <p class="nfc-subtitle">{{ 'nfcTags.description' | translate }}</p>
      </div>

      @if (!nfcReady()) {
      <div class="flex flex-column flex-align-center gap-sm p-md text-center nfc-notice" data-testid="nfc-unavailable">
        <app-icon name="smartphone-nfc" />
        @if (nfcDisabled()) {
        <p>{{ 'scan.nfc.disabledHint' | translate }}</p>
        <div class="flex gap-sm">
          <app-button variant="outlined" (click)="openSettings()" data-testid="nfc-settings-button">
            <app-icon name="settings" />
            {{ 'scan.nfc.openSettings' | translate }}
          </app-button>
          <app-button variant="text" (click)="refreshAvailability()" data-testid="nfc-recheck-button">
            {{ 'nfcTags.recheck' | translate }}
          </app-button>
        </div>
        } @else {
        <p>{{ 'nfcTags.unavailable' | translate }}</p>
        }
      </div>
      } @else {
      <formly-form [form]="optionsForm" [fields]="optionsFields" [model]="optionsModel" data-testid="nfc-options" />

      @if (hasInstances()) {
      <div class="flex flex-align-center flex-justify-space-between gap-md">
        <span class="nfc-progress" data-testid="nfc-progress">
          {{ 'nfcTags.progress' | translate: { done: completedCount().toString(), total: targets().length.toString() } }}
        </span>
        <app-button (click)="writeAll()" [disabled]="busy() || completedCount() === targets().length"
          data-testid="nfc-write-all-button">
          <app-icon name="smartphone-nfc" />
          {{ 'nfcTags.writeAll' | translate }}
        </app-button>
      </div>
      }

      <ul class="nfc-target-list flex flex-column gap-sm" data-testid="nfc-target-list">
        @for (target of targets(); track target.instanceId) {
        <li class="nfc-target flex flex-align-center gap-md p-sm" [class.nfc-target-active]="activeLabel() === target.label"
          [attr.data-testid]="'nfc-target-' + $index" [attr.data-status]="target.status">
          <div class="flex flex-column flex-grow gap-xs">
            <span class="nfc-target-label">{{ target.label }}</span>
            <span class="nfc-target-detail">
              @if (target.code) {
              <span class="nfc-code">{{ target.code }}</span>
              }
              <span [class]="'nfc-status nfc-status-' + target.status">{{ 'nfcTags.status.' + target.status | translate }}</span>
            </span>
            @if (target.messageKey) {
            <span class="nfc-target-message" [attr.data-testid]="'nfc-target-message-' + $index">{{ target.messageKey | translate }}</span>
            }
          </div>
          <app-button variant="outlined" size="sm" (click)="writeOne(target)" [disabled]="busy()"
            [attr.data-testid]="'nfc-write-button-' + $index">
            {{ (target.status === 'written' || target.status === 'already' ? 'nfcTags.rewrite' : 'nfcTags.write') | translate }}
          </app-button>
        </li>
        }
      </ul>
      }

      <!-- Waiting for a tag (on iOS the system NFC sheet covers this) -->
      @if (phase() === 'write' || phase() === 'overwrite' || phase() === 'verify') {
      <div class="flex flex-column flex-align-center gap-md p-lg text-center nfc-active" data-testid="nfc-active">
        <app-icon name="smartphone-nfc" [size]="48" class="nfc-pulse" />
        <p class="nfc-active-label">{{ activeLabel() }}</p>
        <p [attr.data-testid]="'nfc-phase-' + phase()">{{ 'nfcTags.phase.' + phase() | translate }}</p>
        <app-button variant="outlined" (click)="cancel()" data-testid="nfc-cancel-button">
          <app-icon name="x" />
          {{ 'scan.cancel' | translate }}
        </app-button>
      </div>
      }

      <!-- Tag holds something else: confirm before overwriting -->
      @if (overwritePrompt(); as prompt) {
      <div class="flex flex-column gap-md p-lg nfc-confirm" data-testid="nfc-overwrite-prompt">
        <div class="flex flex-align-center gap-sm">
          <app-icon name="alert-triangle" />
          <strong>{{ 'nfcTags.overwrite.title' | translate }}</strong>
        </div>
        @if (prompt.existing.code && prompt.currentTarget) {
        <p>{{ 'nfcTags.overwrite.linked' | translate: { code: prompt.existing.code, target: prompt.currentTarget } }}</p>
        } @else if (prompt.existing.code) {
        <p>{{ 'nfcTags.overwrite.unknownCode' | translate: { code: prompt.existing.code } }}</p>
        } @else {
        <p>{{ 'nfcTags.overwrite.otherData' | translate }}</p>
        }
        <p>{{ 'nfcTags.overwrite.question' | translate: { label: prompt.label } }}</p>
        <div class="flex gap-sm flex-justify-end">
          <app-button variant="outlined" (click)="skipOverwrite()" data-testid="nfc-overwrite-skip">
            {{ 'nfcTags.overwrite.skip' | translate }}
          </app-button>
          <app-button (click)="confirmOverwrite()" data-testid="nfc-overwrite-confirm">
            {{ 'nfcTags.overwrite.confirm' | translate }}
          </app-button>
        </div>
      </div>
      }

      <div class="flex flex-justify-end">
        <app-button variant="text" (click)="done()" [disabled]="busy()" data-testid="nfc-done-button">
          {{ 'nfcTags.done' | translate }}
        </app-button>
      </div>
    </div>
    }
  </app-card-content>
</app-card>
